<template>
  <div id="fieldcustomeditor" class="col-sm-12">
    <input ref="hiddenFieldInput" type="hidden" :name="name" />
    <hr />

    <div v-if="customFields != null">
      <div
        v-for="(field, index) in customFields"
        :key="index"
        :class="['form-group']"
        data-testid="field-item"
      >
        <label class="col-sm-2 control-label input-sm">{{
          field.label || field.key
        }}</label>
        <div class="col-sm-9">
          <input
            v-model="field.value"
            type="text"
            :class="['form-control', 'input-sm', 'context_var_autocomplete']"
            size="100"
            @change="changeField()"
            :data-testid="'field-input-' + index"
          />
        </div>
        <div class="col-sm-1">
          <span
            class="btn btn-xs btn-default"
            :title="$t('message_delete')"
            @click="removeField(field)"
            data-testid="remove-field-button"
          >
            <i class="glyphicon glyphicon-remove"></i
          ></span>
        </div>
        <div v-if="field.desc" class="col-sm-10 col-sm-offset-2 help-block">
          <div class="help-block" data-testid="field-description-help">
            {{ field.desc }}
          </div>
        </div>
      </div>
    </div>

    <btn
      type="primary"
      @click="openNewField()"
      data-testid="add-field-button"
      >{{ $t("message_addField") }}</btn
    >

    <modal
      id="modal-demo"
      ref="modal"
      v-model="modalAddField"
      title="Add Field"
      ok-text="Save"
      :backdrop="true"
      :dismiss-btn="true"
      :keyboard="true"
      cancel-text="Close"
      append-to-body
      data-testid="modal-title"
    >
      <div class="row" style="padding-left: 30px !important">
        <alert
          v-if="duplicate"
          type="warning"
          data-testid="duplicate-warning"
          ref="duplicateWarningRef"
          ><b>Warning!</b> {{ $t("message_duplicated") }}.</alert
        >

        <div class="col-md-10">
          <div v-if="useOptions" class="form">
            <div :class="['form-data']">
              <label class="col-md-4">{{ $t("message_select") }}</label>
              <div class="col-md-8">
                <!--
                  append-to="self" keeps the dropdown overlay inside this
                  modal's stacking context. The default ("body") renders it at
                  z-index 1001, below the surrounding modal (1070), which makes
                  the options render but stay unclickable.
                -->
                <pt-select
                  v-model="selectedField"
                  :options="customOptions"
                  option-label="label"
                  :filter="true"
                  :placeholder="$t('message_select')"
                  append-to="self"
                  data-testid="multiselect"
                />
              </div>
            </div>

            <div :class="['form-data']">
              <label class="col-md-4">{{ $t("message_description") }}</label>
              <div class="col-md-8">
                <input
                  v-model="newFieldDescription"
                  type="text"
                  :class="['form-control']"
                  data-testid="field-description-input"
                />
                <div class="help-block">{{ $t("message_empty") }}</div>
              </div>
            </div>
          </div>

          <div v-if="!useOptions" class="form">
            <div :class="['form-group']">
              <label class="col-md-4">{{ $t("message_fieldLabel") }}</label>
              <div class="col-md-8">
                <input
                  v-model="newLabelField"
                  type="text"
                  :class="['form-control']"
                  data-testid="field-label-input"
                />
              </div>
            </div>
            <div :class="['form-group']">
              <label class="col-md-4">{{ $t("message_fieldKey") }}</label>
              <div class="col-md-8">
                <input
                  v-model="newField"
                  type="text"
                  :class="['form-control']"
                  data-testid="field-key-input"
                />
              </div>
            </div>

            <div :class="['form-group']">
              <label class="col-md-4">{{ $t("message_description") }}</label>
              <div class="col-md-8">
                <input
                  v-model="newFieldDescription"
                  type="text"
                  :class="['form-control']"
                  data-testid="field-description-input"
                />
                <div class="help-block">{{ $t("message_empty") }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div>
          <button
            type="button"
            class="btn btn-default reset_page_confirm"
            @click="modalAddField = false"
            data-testid="cancel-button"
          >
            {{ $t("message_cancel") }}
          </button>

          <button
            type="button"
            class="btn btn-cta reset_page_confirm"
            @click="addField()"
            data-testid="confirm-add-field-button"
          >
            {{ $t("message_add") }}
          </button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Btn, Alert, Modal } from "uiv";
import PtSelect from "../primeVue/PtSelect/PtSelect.vue";

interface CustomField {
  key?: string;
  label?: string;
  value?: string;
  desc?: string;
}

export default defineComponent({
  name: "DynamicFormPluginProp",
  components: {
    PtSelect,
    Btn,
    Alert,
    Modal,
  },
  props: {
    fields: {
      type: String,
      required: true,
    },
    options: {
      type: String,
      required: false,
    },
    hasOptions: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      customFields: [] as CustomField[],
      customOptions: [] as any[],
      useOptions: false,
      modalAddField: false,
      duplicate: false,
      newField: "",
      newLabelField: "",
      newFieldDescription: "",
      selectedField: { value: "", label: "" },
    };
  },
  watch: {
    fields(newFields: string) {
      // Keep local state in sync if the prop changes after mount (e.g. the
      // parent round-trips the value through its own v-model chain). Skip
      // when the incoming value already matches what we just emitted
      // ourselves, to avoid fighting with in-flight edits.
      if (newFields === JSON.stringify(this.customFields)) {
        return;
      }
      this.syncFieldsFromProp(newFields);
    },
  },
  mounted() {
    if (this.hasOptions === "true") {
      this.useOptions = true;
    }
    this.syncFieldsFromProp(this.fields);

    if (
      this.useOptions &&
      this.options !== null &&
      this.options !== undefined &&
      this.options !== ""
    ) {
      const optionsObject = JSON.parse(this.options!);
      const options = Object.keys(optionsObject).map((key: any) => {
        const data = optionsObject[key];
        return { value: key, label: data };
      });
      this.customOptions = options;
    }
  },
  beforeUnmount() {
    this.customFields = null as any;
  },
  methods: {
    syncFieldsFromProp(fields: string) {
      if (fields == null || fields === "") {
        // Clearing the prop must clear the list too, otherwise the previous
        // fields stay on screen after the parent resets the value.
        this.customFields = [];
        return;
      }
      const customFieldsObject = JSON.parse(fields);
      if (customFieldsObject != null) {
        const parsedFields = Object.keys(customFieldsObject).map((key: any) => {
          const value = customFieldsObject[key];
          if (value.desc == null) {
            value.desc = this.$t("message_fieldKeyDescription", [value.key]);
          }
          return value;
        });
        this.customFields = parsedFields;
      }
    },
    openNewField() {
      this.modalAddField = true;
    },
    addField() {
      let field = {} as CustomField;
      this.duplicate = false;

      if (this.useOptions) {
        if (this.selectedField !== null) {
          const newField = this.selectedField;

          let description = this.newFieldDescription;
          if (description == "") {
            description = this.$t("message_fieldKeyOnlyDescription", [
              newField.value,
            ]);
          } else {
            description = this.$t("message_fieldKeyAppendedDescription", [
              description,
              newField.value,
            ]);
          }

          field = {
            key: newField.value,
            label: newField.label,
            desc: description,
          };
        }
      } else {
        let description = this.newFieldDescription;
        if (description == "") {
          description = this.$t("message_fieldKeyOnlyDescription", [
            this.newField,
          ]);
        } else {
          description = this.$t("message_fieldKeyAppendedDescription", [
            description,
            this.newField,
          ]);
        }

        field = {
          key: this.newField,
          label: this.newLabelField,
          value: "",
          desc: description,
        };
      }
      let exists = false;
      this.customFields.forEach((row: any) => {
        if (field.key === row.key) {
          exists = true;
        }
      });

      if (!exists) {
        this.customFields.push(field);
        this.newField = "";
        this.newLabelField = "";
        this.newFieldDescription = "";
        this.modalAddField = false;
        this.refreshPlugin();
      } else {
        this.duplicate = true;
      }
    },
    removeField(row: any) {
      const fields = [] as CustomField[];
      this.customFields.forEach((field: CustomField) => {
        if (field.key !== row.key) {
          fields.push(field);
        }
      });
      this.customFields = fields;
      this.refreshPlugin();
    },
    changeField() {
      this.refreshPlugin();
    },
    refreshPlugin() {
      const fieldsJson = JSON.stringify(this.customFields);
      const hiddenFieldInput = this.$refs.hiddenFieldInput as HTMLInputElement;
      if (hiddenFieldInput) {
        hiddenFieldInput.value = fieldsJson;
      }
      this.$emit("update:modelValue", fieldsJson);
    },
  },
});
</script>
<style scoped>
.form-data {
  padding-bottom: 30px;
  margin-bottom: 20px;
}
</style>
